import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Container, ProgressBar, Modal as RBModal } from 'react-bootstrap';
import { DefaultButton } from '../components/DefaultButton';
import { Col, Row } from '../components/Grid';
import TextInput from '../components/TextInput';
import PropTypes from 'prop-types';
import PasswordInput from '../components/PasswordInput';

const DialogManagementContext = createContext();

/**
 * Provider to manage nesting of dialogs - only one shows at time
 * @component
 */
export const DialogManagementProvider = ({ children }) => {
    const [dialogStack, setDialogStack] = useState([]);
    
    const active = useCallback((dialogId) => {
        return dialogStack[dialogStack.length - 1] === dialogId;
    }, [dialogStack]);

    const push = useCallback((dialogId) => {
        setDialogStack((prevStack) => {
            if (prevStack.includes(dialogId)) {
                return prevStack;
            }
            return [...prevStack, dialogId]
        });
    }, []);

    const close = useCallback((dialogId) => {
        setDialogStack(prevStack => {
            if (prevStack.includes(dialogId)) {
                return prevStack.filter(id => id !== dialogId);
            }
            return prevStack;
        })
    }, []);


    const value = useMemo(() => ({ dialogStack, active, push, close }), [dialogStack, active, push, close]);
    return <DialogManagementContext.Provider value={value}>
        {children}
    </DialogManagementContext.Provider>
}
DialogManagementProvider.propTypes = {
    children: PropTypes.node
}

const useDialogStack = () => useContext(DialogManagementContext);

/**
 * Basic dialog wrapper which exists to enable support for dialog nesting via the DiloagManagementProvider
 * @component
 */
export const Modal = ({ show, children, ...props }) => {
    const dialogStack = useDialogStack();
    
    // hold the UID outside of React State so that there are no dependencies that could change - this a single static value
    const uid = useRef(crypto.randomUUID());

    // we need to make sure that we tell the dialogStack that we have close if we are unmounted without show becomming false
    // in this case we need the most recent dialogStack to call close on, but we do not want our useEffect clean-up to fire when
    // dialog stack instance changes because that just means dialogs are hiding / showing and constantly registering all open dialogs
    // creates a race between dialogs for registration rather than preserving chronological opening order (the intent of the stack).
    // Therefore, capture the stack into a ref that can be read from a useEffect cleanup of a no-dep useEffect to handle just the
    // unmount case.
    // DO NOT CHANGE THE STRUCTURE WITHOUT CONSULTATION - this is a tricky case to handle
    const stackRef = useRef(dialogStack);
    stackRef.current = dialogStack;

    useEffect(() => {
        return () => stackRef.current?.close(uid.current)
    }, []);

    useEffect(() => {
        if (show) {
            dialogStack?.push(uid.current);
        } else {
            dialogStack?.close(uid.current);
        }
    }, [show, dialogStack])

    const hide = dialogStack && !dialogStack?.active(uid.current);

    //WARNING: invisibility is required not d-none, because layout changes could trigger unexpected behaviour i.e resize aware divs firing
    return <RBModal show={show} dialogClassName={hide ? 'invisible' : null} backdropClassName={hide ? 'invisible' : null} {...props}>
        {children}
    </RBModal>
}
Modal.Body = RBModal.Body;
Modal.Footer = RBModal.Footer;
Modal.Header = RBModal.Header;
Modal.Title = RBModal.Title;
Modal.propTypes = {
    show: PropTypes.bool,
    children: PropTypes.node
}

/**
 * Dialog to show React controls
 * @component
 */
export const ReactNodeDialog = ({ title, children, kind, size, showModal, onSave, onClose, footerControls, invalid, style}) => {
    return <Modal show={showModal} size={size}>
        {title && <Modal.Header>
            <Modal.Title>{title}</Modal.Title>
        </Modal.Header>}
        <Modal.Body style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', ...style }}>
            {showModal ? children : null}
        </Modal.Body>
        <Modal.Footer>
            <Row className="w-100">
                { invalid && invalid !== true && <Row className="w-100">
                    <Col>
                        <Alert variant='danger'>{invalid}</Alert>
                    </Col>
                </Row> }
                <Row className="w-100">
                    <Col xs>{footerControls}</Col>
                    <Col xs="auto">
                        {onSave ? <DefaultButton className="mx-2" kind={kind ?? "save" } disabled={invalid} onClick={() => onSave()}></DefaultButton> : null}
                        {onClose ? <DefaultButton kind="close" onClick={() => onClose()}></DefaultButton> : null}
                    </Col>
                </Row>
            </Row>
        </Modal.Footer>
    </Modal>
}

ReactNodeDialog.propTypes = {
    /** Title for the dialog */
    title: PropTypes.node.isRequired,

    /** The body of the dialog box */
    children: PropTypes.node,

    /** The dialog box size on screen */
    size: PropTypes.oneOf(['sm', 'md', 'lg', 'xlg']),

    /** Controls whether the modal is visiable */
    showModal: PropTypes.bool.isRequired,

    /** Function to call when the modal is closed via the save button. */
    onSave: PropTypes.func,

    /** Function to call when the modal is closed via the close button. */
    onClose: PropTypes.func,

    /** A node to be rendered on the right side of the modal footer */
    footerControls: PropTypes.node,

    /** A truth value will disable the save button, a non-boolean will be treated as
     * an error to be displayed in addition to disabling the save button */
    invalid: PropTypes.any,

    /** A flag to override button kind of the save button*/
    kind: PropTypes.string,

    /** Style option to change rendering of the control */
    style: PropTypes.object,
}

/**
 * Dialog to aquire a text message from the user
 * @component 
 */
export const TextPromptDialog = ({ showModal, onClose, title, message, placeholder, initialText, maxLength }) => {
    const [text, setText] = useState(initialText);

    const handleClose = (result) => {
        setText(null);
        onClose(result);
    }

    return (
        <Modal show={showModal} onHide={_ => handleClose(false)}>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container fluid>
                    <Row><Col>{message}</Col></Row>
                    <Row><Col><TextInput value={text} live={true} placeholder={placeholder} maxLength={maxLength} onChange={setText} hideLabel={true} required={true}></TextInput></Col></Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <DefaultButton kind="ok" onClick={_ => handleClose(text)} disabled={!text}></DefaultButton>
                <DefaultButton kind="cancel" onClick={_ => handleClose(false)}></DefaultButton>
            </Modal.Footer>
        </Modal>
    )
}

TextPromptDialog.propTypes = {
    /** Controls whether the modal is visiable */
    showModal: PropTypes.bool.isRequired,

    /** Function to call when the modal is closed. Will pass the input text if ok is pressed, false otherwise */
    onClose: PropTypes.func.isRequired,

    /** Title for the dialog */
    title: PropTypes.node.isRequired,

    /** Text to be displayed by the dialog */
    message: PropTypes.string.isRequired,

    /** Placeholder text for the input box */
    placeholder: PropTypes.string.isRequired,

    /** Optional initial text value */
    initialText: PropTypes.string,

    /** Maxium number of characters allowed in the input box */
    maxLength: PropTypes.number.isRequired
}

/**
 * Dialog to aquire a text message from the user
 * @component 
 */
export const PasswordPromptDialog = ({ showModal, onClose, title, message, placeholder }) => {
    const [text, setText] = useState();

    const handleClose = (result) => {
        setText(null);
        onClose(result);
    }

    return (
        <Modal show={showModal} onHide={_ => handleClose(false)}>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container fluid>
                    <Row><Col>{message}</Col></Row>
                    <Row><Col><PasswordInput value={text} live={true} placeholder={placeholder} onChange={setText} hideLabel={true} required={true}></PasswordInput></Col></Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <DefaultButton kind="ok" onClick={_ => handleClose(text)} disabled={!text}></DefaultButton>
                <DefaultButton kind="cancel" onClick={_ => handleClose(false)}></DefaultButton>
            </Modal.Footer>
        </Modal>
    )
}

PasswordPromptDialog.propTypes = {
    /** Controls whether the modal is visiable */
    showModal: PropTypes.bool.isRequired,

    /** Function to call when the modal is closed. Will pass the input text if ok is pressed, false otherwise */
    onClose: PropTypes.func.isRequired,

    /** Title for the dialog */
    title: PropTypes.node.isRequired,

    /** Text to be displayed by the dialog */
    message: PropTypes.string.isRequired,

    /** Placeholder text for the input box */
    placeholder: PropTypes.string.isRequired
}

/**
 * Dialog to request confirmation from user about an action
 * @component 
 */
export const ConfirmDialog = ({ showModal, onClose, title, message, okLabel, cancelLabel }) => {
    return (
        <Modal show={showModal} onHide={_ => onClose(false)}>
            {title && <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>}
            <Modal.Body>
                {message}
            </Modal.Body>
            <Modal.Footer>
                <DefaultButton kind="ok" label={okLabel} onClick={_ => onClose(true)}></DefaultButton>
                <DefaultButton kind="cancel" label={cancelLabel} onClick={_ => onClose(false)}></DefaultButton>
            </Modal.Footer>
        </Modal>
    )
}

ConfirmDialog.propTypes = {
    /** Controls whether the modal is visiable */
    showModal: PropTypes.bool.isRequired,

    /** Function to call when the modal is closed. Will pass true if ok is pressed, false otherwise */
    onClose: PropTypes.func.isRequired,

    /** Title for the dialog */
    title: PropTypes.node,

    /** Text to be displayed by the dialog */
    message: PropTypes.string.isRequired,

    /** The string to render on the ok button */
    okLabel: PropTypes.string,

    /** The string to render on the cancel button */
    cancelLabel: PropTypes.string
}

/**
 * Dialog to display a message to the user
 * @component 
 */
export const ShowMessageDialog = ({ showModal, onClose, title, message }) => {
    return (
        <Modal show={showModal} onHide={onClose}>
            { title && <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header> }
            <Modal.Body>
                {message}
            </Modal.Body>
            <Modal.Footer>
                <DefaultButton kind="ok" onClick={onClose}></DefaultButton>
            </Modal.Footer>
        </Modal>
    )
}

ShowMessageDialog.propTypes = {
    /** Controls whether the modal is visiable */
    showModal: PropTypes.bool.isRequired,

    /** Function to call when the modal is closed */
    onClose: PropTypes.func.isRequired,

    /** Title for the dialog */
    title: PropTypes.node.isRequired,

    /** Text to be displayed by the dialog */
    message: PropTypes.string.isRequired,

}

export const ProgressDialog = ({ progress }) => {
    if (typeof progress === 'number' && !isNaN(progress)) {
        return <Modal show centered>
            <Modal.Body>
                <ProgressBar now={Math.round(progress)} label={`${Math.round(progress)}%`}></ProgressBar>
            </Modal.Body>
        </Modal>
    }
    return null;
}
ProgressDialog.propTypes = {
    /**
     * The progress as a percent of 100 (will be displayed as an integer)
     */
    progress: PropTypes.number
}